import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { Box, Button, Typography, styled } from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import heic2any from "heic2any";
import FileUploadBox from "./components/FileUploadBox";
import FormPageHeader from "./quizSystem/components/FormPageHeader";
import DraggableSelectList from "./quizSystem/questionsSection/components/QuestionListDropdown";
import { useSelector, useDispatch } from "react-redux";
import { fetchActiveQuestions } from "../store/reducers/questionsSectionSlice";
import { fetchCombinedCode } from "../store/reducers/countryLanguageSlice";
import { MdDeleteOutline } from "react-icons/md";

import {
  collectNewTargetCountries,
  fetchTargetCountryOptions,
  getCountryLabels,
  mergeCountryOptions,
  normalizeCountrySelections,
  postNewTargetCountries,
} from "../utils/targetCountry";
import {
  DEFAULT_COUNTRY_LANGUAGE_CODE,
  findDefaultCountryLanguageOption,
  isDefaultCountryLanguageSection,
} from "../utils/defaultCountryLanguage";

const ClientType = [
  { value: "Business", label: "Business" },
  { value: "Sport", label: "Sport" },
  { value: "Private", label: "Private" },
];

const Purpose = [
  { value: "First Aid", label: "First Aid" },
  { value: "Emergency Service", label: "Emergency Service" },
  { value: "Cyber", label: "Cyber" },
  { value: "Machines", label: "Machines" },
];

const Lang = [
  { value: "Danish", label: "Danish" },
  { value: "English", label: "English" },
];

const Format = [
  { value: "A6", label: "A6" },
  { value: "A7", label: "A7" },
  { value: "Tailored", label: "Tailored" },
];

const Standard = [
  { value: "Standard", label: "Standard" },
  { value: "Tailored", label: "Tailored" },
];

const findOption = (options, value) => {
  if (!value) return null;
  return (
    options.find(
      (option) => option.label === value || option.value === value,
    ) || { label: value, value }
  );
};

const normalizeMultiSelectValue = (value, options) => {
  if (!value) return [];

  let parsedValue = value;

  if (typeof value === "string") {
    try {
      parsedValue = JSON.parse(value);
    } catch {
      parsedValue = [value];
    }
  }

  if (!Array.isArray(parsedValue)) {
    parsedValue = [parsedValue];
  }

  return parsedValue
    .map((item) => {
      if (!item) return null;
      if (typeof item === "object" && item.label) {
        return findOption(options, item.label);
      }
      return findOption(options, item);
    })
    .filter(Boolean);
};

const getLabels = (selected) =>
  Array.isArray(selected) ? selected.map((item) => item.label) : [];

const CircleContainer = styled("div")({
  width: 150,
  height: 150,
  borderRadius: "50%",
  border: "2px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#555",
  fontSize: "14px",
  cursor: "pointer",
  position: "relative",
  backgroundColor: "#f9f9f9",
  overflow: "hidden",
});

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "50%",
  position: "absolute",
  top: 0,
  left: 0,
});

const MAX_ICON_FILE_SIZE = 50 * 1024 * 1024;

const parseCombinedCode = (combinedCode = "", id = null) => {
  const [countryCode = "", langCode = ""] = combinedCode.split("-");

  return {
    value: combinedCode,
    label: combinedCode,
    id,
    countryCode,
    langCode,
  };
};

const createCountryPdfSection = (sectionId = 1, overrides = {}) => ({
  id: sectionId,
  sectionId,
  country_language_id: "",
  combinedCode: "",
  version: "",
  languageSpecificName: "",
  languageSpecificShortName: "",
  frontFile: null,
  backFile: null,
  collapsed: true,
  ...overrides,
});

const getSectionTitle = (section, fallbackIndex) => {
  const countryLabel = section.combinedCode;
  const versionLabel = section.version ? `v${section.version}` : "";
  const title = [countryLabel, versionLabel].filter(Boolean).join(" - ");

  return title || `Country/language ${fallbackIndex + 1}`;
};

const formPanelStyle = {
  background: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "10px",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
  marginBottom: "18px",
  padding: "18px",
};

const pageHeaderWrapStyle = {
  marginTop: "12px",
};

const sectionPanelStyle = {
  ...formPanelStyle,
  background: "#f8fafc",
  borderColor: "#e2e8f0",
  overflow: "hidden",
  padding: 0,
};

const sectionTitleStyle = {
  color: "#0f172a",
  fontSize: "16px",
  fontWeight: 700,
  margin: "0 0 14px",
};

const sectionHeaderStyle = {
  alignItems: "center",
  background: "#f8fafc",
  display: "flex",
  justifyContent: "space-between",
  padding: "12px 16px 8px",
};

const sectionBodyStyle = {
  background: "#fff",
  padding: "0 18px 18px",
};

const iconButtonStyle = {
  alignItems: "center",
  background: "transparent",
  border: 0,
  borderRadius: "6px",
  color: "#f85c63",
  display: "inline-flex",
  height: "32px",
  justifyContent: "center",
  padding: 0,
  width: "32px",
};

const removeButtonStyle = {
  background: "#fff",
  border: "1px solid #ff5c66",
  borderRadius: "6px",
  color: "#ff5c66",
  fontWeight: 500,
  minHeight: "34px",
  padding: "4px 14px",
};

const AddActionCard = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { search } = useLocation();

  const paramIdSeprator = (param) => {
    const paramId = param.split("=");
    return paramId[1];
  };

  const [clientTypes, setClientTypes] = useState([ClientType[0]]);
  const [formats, setFormats] = useState([Format[0]]);
  const [purposes, setPurposes] = useState(null);
  const [langs, setLangs] = useState([Lang[0]]);
  const [remark, setRemark] = useState("");
  const [countrys, setCountrys] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [existingCountryOptions, setExistingCountryOptions] = useState([]);
  const [standards, setStandards] = useState(Standard[0]);
  const [versionNumber, setVersionNumber] = useState("");
  const [ac_no, setAc_no] = useState();
  const [files] = useState([]);
  const [engName, setEngName] = useState("");
  const [engShortName, setEngShortName] = useState("");
  const [danishName, setDanishName] = useState("");
  const [danishShortName, setDanishShortName] = useState("");
  const [smallIconFile, setSmallIconFile] = useState(null);
  const [smallIconPreview, setSmallIconPreview] = useState("");
  const [smallIconError, setSmallIconError] = useState("");
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [countryPdfSections, setCountryPdfSections] = useState([]);
  const [options, setOptions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [defaultQuestionIds, setDefaultQuestionIds] = useState([]);
  const [sectionErrors, setSectionErrors] = useState({});
  const { activeQuestions } = useSelector((state) => state.questions);
  const { dropdownList = [] } = useSelector((state) => state.countryLanguage);
  const combinedCodeOptions = useMemo(
    () =>
      (dropdownList || []).map((item) =>
        parseCombinedCode(
          item.combined_code,
          item.id || item.country_language_id || item.countryLanguageId,
        ),
      ),
    [dropdownList],
  );

  const getCountryLanguageId = (section) =>
    section.country_language_id ||
    combinedCodeOptions.find((option) => option.value === section.combinedCode)
      ?.id ||
    "";

  const getSectionCountryLanguageOption = (section) => {
    const countryLanguageId = getCountryLanguageId(section);

    return (
      combinedCodeOptions.find(
        (option) => `${option.id}` === `${countryLanguageId}`,
      ) || null
    );
  };

  const selectedCountryLanguageIds = [
    ...new Set(
      countryPdfSections
        .map((section) => `${getCountryLanguageId(section)}`)
        .filter(Boolean),
    ),
  ];
  const allCountryLanguagesUsed =
    combinedCodeOptions.length > 0 &&
    countryPdfSections.length >= combinedCodeOptions.length;
  const defaultCountryLanguageOption = useMemo(
    () => findDefaultCountryLanguageOption(combinedCodeOptions),
    [combinedCodeOptions],
  );
  const defaultCountryPdfSection = useMemo(
    () =>
      countryPdfSections.find((section) =>
        isDefaultCountryLanguageSection(section, combinedCodeOptions),
      ) || null,
    [combinedCodeOptions, countryPdfSections],
  );
  const headerEnglishName =
    defaultCountryPdfSection?.languageSpecificName || engName;
  const headerEnglishShortName =
    defaultCountryPdfSection?.languageSpecificShortName || engShortName;

  const getSectionCountryLanguageOptions = (currentSection) => {
    const currentCountryLanguageId = `${getCountryLanguageId(currentSection)}`;
    const selectedIds = countryPdfSections
      .filter((section) => section.id !== currentSection.id)
      .map((section) => `${getCountryLanguageId(section)}`)
      .filter(Boolean);

    return combinedCodeOptions.filter(
      (option) =>
        `${option.id}` === currentCountryLanguageId ||
        !selectedIds.includes(`${option.id}`),
    );
  };

  useEffect(() => {
    dispatch(fetchActiveQuestions());
    dispatch(fetchCombinedCode());
  }, [dispatch]);

  useEffect(() => {
    if (!defaultCountryLanguageOption) return;

    setCountryPdfSections((prev) => {
      if (
        prev.some((section) =>
          isDefaultCountryLanguageSection(section, combinedCodeOptions),
        )
      ) {
        return prev;
      }

      const nextSectionId =
        Math.max(0, ...prev.map((section) => section.sectionId || 0)) + 1;

      return [
        createCountryPdfSection(nextSectionId, {
          country_language_id: defaultCountryLanguageOption.id || "",
          combinedCode: defaultCountryLanguageOption.value,
          languageSpecificName: engName,
          languageSpecificShortName: engShortName,
          collapsed: false,
        }),
        ...prev,
      ];
    });
  }, [
    combinedCodeOptions,
    countryPdfSections,
    defaultCountryLanguageOption,
    engName,
    engShortName,
  ]);

  useEffect(() => {
    if (activeQuestions) {
      const formatted = [...activeQuestions]
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map((q) => ({
          label: `ID# ${q.id} - ${q.question}`,
          value: q.id,
        }));

      setOptions(formatted);
    }
  }, [activeQuestions]);

  const handleSelectedIds = (ids) => {
    setSelectedQuestionIds(ids);
  };

  const jwtToken = localStorage.getItem("jwt_token");
  const id = paramIdSeprator(search);

  const loadTargetCountryOptions = async () => {
    const apiCountryOptions = await fetchTargetCountryOptions(jwtToken);
    const mergedOptions = mergeCountryOptions(apiCountryOptions);

    setExistingCountryOptions(mergedOptions);
    setCountryOptions((prev) => mergeCountryOptions(prev, mergedOptions));
  };

  const syncNewTargetCountries = async () => {
    const newCountries = collectNewTargetCountries(
      [countrys],
      existingCountryOptions,
    );
    const response = await postNewTargetCountries(jwtToken, newCountries);

    if (response?.error) {
      throw new Error(response.error);
    }

    if (newCountries.length) {
      const mergedOptions = mergeCountryOptions(
        existingCountryOptions,
        newCountries,
      );
      setExistingCountryOptions(mergedOptions);
      setCountryOptions((prev) => mergeCountryOptions(prev, mergedOptions));
    }
  };

  const addCountryPdfSection = () => {
    if (allCountryLanguagesUsed) return;

    const nextSectionId = search
      ? Math.max(
          0,
          ...countryPdfSections.map((section) => section.sectionId || 0),
        ) + 1
      : countryPdfSections.length + 1;

    setCountryPdfSections((prev) => [
      ...prev,
      createCountryPdfSection(nextSectionId, {
        collapsed: false,
      }),
    ]);
  };

  const removeCountryPdfSection = (sectionId) => {
    setCountryPdfSections((prev) => {
      const section = prev.find((item) => item.id === sectionId);
      if (isDefaultCountryLanguageSection(section, combinedCodeOptions)) {
        return prev;
      }

      const filteredSections = prev.filter(
        (section) => section.id !== sectionId,
      );

      if (search) {
        return filteredSections;
      }

      return filteredSections.map((section, index) => ({
        ...section,
        id: index + 1,
        sectionId: index + 1,
      }));
    });
  };

  const updateCountryPdfSection = (sectionId, key, value) => {
    setCountryPdfSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, [key]: value } : section,
      ),
    );
    setSectionErrors((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [key]: "",
      },
    }));
  };

  const updateCountryPdfSectionLanguage = (sectionId, selectedOption) => {
    setCountryPdfSections((prev) =>
      prev.map((section) =>
        section.id === sectionId &&
        !isDefaultCountryLanguageSection(section, combinedCodeOptions)
          ? {
              ...section,
              country_language_id: selectedOption?.id || "",
              combinedCode: selectedOption?.value || "",
            }
          : section,
      ),
    );
    setSectionErrors((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        country_language_id: "",
      },
    }));
  };

  const toggleCountryPdfSection = (sectionId) => {
    setCountryPdfSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, collapsed: !section.collapsed }
          : section,
      ),
    );
  };

  const validateAdditionalSections = () => {
    const nextErrors = {};
    const hasDefaultSection = countryPdfSections.some((section) =>
      isDefaultCountryLanguageSection(section, combinedCodeOptions),
    );

    if (!defaultCountryLanguageOption || !hasDefaultSection) {
      nextErrors.default_country_language = {
        country_language_id: `${DEFAULT_COUNTRY_LANGUAGE_CODE} country/language section is required`,
      };
    }

    countryPdfSections.forEach((section) => {
      if (!getCountryLanguageId(section)) {
        nextErrors[section.id] = {
          ...nextErrors[section.id],
          country_language_id: "Country/Language is required",
        };
      }

      if (!section.languageSpecificName?.trim()) {
        nextErrors[section.id] = {
          ...nextErrors[section.id],
          languageSpecificName: "Language specific name is required",
        };
      }
    });

    setSectionErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const appendPdfField = (bodyFormData, key, value) => {
    if (!value) {
      bodyFormData.append(key, "none");
      return;
    }

    bodyFormData.append(key, value);
  };

  const extractCardIdFromResponse = (responseData) =>
    responseData?.card_id ||
    responseData?.id ||
    responseData?.data?.card_id ||
    responseData?.data?.id ||
    responseData?.data?.actioncard_id ||
    responseData?.data?.action_id ||
    null;

  const submitActionCardSmallIcon = async (cardId) => {
    const iconUrl = `${process.env.REACT_APP_API_URL}/actioncard-icon`;
    const iconFormData = new FormData();

    iconFormData.append("card_id", cardId);
    iconFormData.append("icon", smallIconFile || "none");

    const iconResponse = await fetch(iconUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
      },
      body: iconFormData,
    });

    return iconResponse.json();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (file.size > MAX_ICON_FILE_SIZE) {
      setSmallIconError("File size must be less than 50MB");
      e.target.value = "";
      return;
    }

    try {
      let finalFile = file;

      if (file.type === "image/heic" || file.type === "image/heif") {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });

        finalFile = new File(
          [convertedBlob],
          file.name.replace(/\.[^/.]+$/, ".jpeg"),
          { type: "image/jpeg" },
        );
      }

      setSmallIconFile(finalFile);
      setSmallIconPreview(URL.createObjectURL(finalFile));
      setSmallIconError("");
    } catch (error) {
      setSmallIconError("Unsupported image format or conversion failed.");
      toast.error("Unsupported image format or conversion failed.");
    }
  };

  const handleRemoveImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSmallIconFile(null);
    setSmallIconPreview("");
    setSmallIconError("");

    const input = document.getElementById("small-icon-upload-image");
    if (input) input.value = "";
  };

  const submitActionCardPdfSections = async (cardId, isUpdate = false) => {
    const pdfUrl = `${process.env.REACT_APP_API_URL}/actioncard-pdf`;
    const pdfFormData = new FormData();
    const countrySectionsPayload = countryPdfSections.map((section, index) => {
      const resolvedSectionId =
        isUpdate && section.sectionId ? section.sectionId : index + 1;

      appendPdfField(
        pdfFormData,
        `front_pdf_${resolvedSectionId}`,
        section.frontFile,
      );
      appendPdfField(
        pdfFormData,
        `back_pdf_${resolvedSectionId}`,
        section.backFile,
      );

      return {
        section_id: resolvedSectionId,
        country_language_id: Number(getCountryLanguageId(section)),
        name: section.languageSpecificName || "",
        short_name: section.languageSpecificShortName || "",
        version: section.version || "",
      };
    });

    pdfFormData.append("card_id", cardId);
    pdfFormData.append(
      "country_sections",
      JSON.stringify(countrySectionsPayload),
    );

    const pdfResponse = await fetch(pdfUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
      },
      body: pdfFormData,
    });

    return pdfResponse.json();
  };

  const appendCountryPdfSections = (bodyFormData) => {
    bodyFormData.append(
      "target_country",
      JSON.stringify(getCountryLabels(countrys)),
    );
    bodyFormData.append("standard_or_tailored", standards?.label || "");
    bodyFormData.append("version", versionNumber || "");
    appendPdfField(bodyFormData, "front_pdf", frontFile);
    appendPdfField(bodyFormData, "back_pdf", backFile);
  };

  const appendCommonFormData = (bodyFormData) => {
    bodyFormData.append("english_name", headerEnglishName);
    bodyFormData.append("english_short_name", headerEnglishShortName);
    bodyFormData.append("danish_name", danishName);
    bodyFormData.append("danish_short_name", danishShortName);
    bodyFormData.append("client_type", JSON.stringify(getLabels(clientTypes)));
    bodyFormData.append("format", JSON.stringify(getLabels(formats)));
    bodyFormData.append("purpose_type", purposes?.label || "");
    bodyFormData.append("language", JSON.stringify(getLabels(langs)));
    bodyFormData.append("remarks", remark);
    bodyFormData.append("ac_no", ac_no);

    files.forEach((file) => bodyFormData.append("ac_document", file));

    appendCountryPdfSections(bodyFormData);
    bodyFormData.append("question_ids", JSON.stringify(selectedQuestionIds));
  };

  useEffect(() => {
    const initializePage = async () => {
      await loadTargetCountryOptions();
      AcNumberInAddData();
      if (id) {
        update(id);
      }
    };

    initializePage();
  }, []);

  const postDataApi = async () => {
    const url = `${process.env.REACT_APP_API_URL}/actioncard`;
    const bodyFormData = new FormData();

    appendCommonFormData(bodyFormData);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
        body: bodyFormData,
      });
      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
        return;
      }

      const cardId = extractCardIdFromResponse(data);
      if (!cardId) {
        toast.error(
          "Action card created but card_id not found for file uploads",
        );
        return;
      }

      const iconData = await submitActionCardSmallIcon(cardId);
      if (iconData.error) {
        toast.error(iconData.error);
        return;
      }

      const pdfData = await submitActionCardPdfSections(cardId, false);
      if (pdfData.error) {
        toast.error(pdfData.error);
        return;
      }

      toast.success("Data Added");
      history.push("/action-cards");
    } catch {
      toast.error("Something went wrong");
    }
  };

  const update = async (actionCardId) => {
    const url = `${process.env.REACT_APP_API_URL}/actioncard/${actionCardId}`;
    fetch(url, {
      method: "get",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        const codeMessage = data.data.actioncard_details;

        setEngName(codeMessage.english_name);
        setEngShortName(codeMessage.english_short_name);
        setDanishName(codeMessage.danish_name || "");
        setDanishShortName(codeMessage.danish_short_name || "");
        setRemark(codeMessage.remarks);
        setSmallIconFile(codeMessage.icon);
        setSmallIconPreview(codeMessage.icon);

        setClientTypes(
          normalizeMultiSelectValue(codeMessage.client_type, ClientType),
        );
        setFormats(normalizeMultiSelectValue(codeMessage.format, Format));
        setLangs(normalizeMultiSelectValue(codeMessage.language, Lang));

        setPurposes(
          codeMessage.purpose_type
            ? findOption(Purpose, codeMessage.purpose_type)
            : null,
        );
        setCountrys(
          normalizeCountrySelections(
            codeMessage.target_company,
            countryOptions,
          ),
        );
        setStandards(
          findOption(
            Standard,
            codeMessage.standard_and_tailored ||
              codeMessage.standard_or_tailored,
          ) || Standard[0],
        );
        setVersionNumber(
          codeMessage.version !== undefined && codeMessage.version !== null
            ? String(codeMessage.version)
            : "",
        );
        setFrontFile(codeMessage.front_pdf || null);
        setBackFile(codeMessage.back_pdf || null);
        setAc_no(codeMessage.ac_no);

        if (codeMessage.question_ids) {
          const ids = Array.isArray(codeMessage.question_ids)
            ? codeMessage.question_ids
            : JSON.parse(codeMessage.question_ids);

          setSelectedQuestionIds(ids);
          setDefaultQuestionIds(ids);
        }

        const defaultTargetCountry = Array.isArray(codeMessage.target_company)
          ? codeMessage.target_company.join(", ")
          : `${codeMessage.target_company || ""}`;
        const defaultStandardValue =
          codeMessage.standard_and_tailored ||
          codeMessage.standard_or_tailored ||
          "";
        const defaultVersionValue =
          codeMessage.version !== undefined && codeMessage.version !== null
            ? String(codeMessage.version)
            : "";
        const countrySectionSource = Array.isArray(codeMessage.country_section)
          ? codeMessage.country_section
          : [];

        const filteredCountrySections = countrySectionSource.filter(
          (section) => {
            const sectionTargetCountry = `${section.target_country || ""}`;
            const sectionStandardValue =
              section.standard_and_tailored ||
              section.standard_or_tailored ||
              "";
            const sectionVersionValue =
              section.version !== undefined && section.version !== null
                ? String(section.version)
                : "";

            const looksLikeDefaultSection =
              sectionTargetCountry === defaultTargetCountry &&
              sectionStandardValue === defaultStandardValue &&
              sectionVersionValue === defaultVersionValue &&
              !section.front_pdf &&
              !section.back_pdf;

            return !looksLikeDefaultSection;
          },
        );

        const hydratedSections = filteredCountrySections.map(
          (section, index) => {
            const normalizedCountries = normalizeCountrySelections(
              section.target_country || section.target_company,
              countryOptions,
            );
            const countryLanguageOption = combinedCodeOptions.find(
              (item) =>
                `${item.id}` === `${section.country_language_id}` ||
                item.value === section.combined_code ||
                item.value === section.combinedCode,
            );

            return createCountryPdfSection(
              section.section_id || section.sectionId || index + 1,
              {
                sectionId: section.section_id || section.sectionId || index + 1,
                countries: normalizedCountries,
                country_language_id:
                  section.country_language_id ||
                  countryLanguageOption?.id ||
                  "",
                combinedCode:
                  section.combinedCode ||
                  section.combined_code ||
                  countryLanguageOption?.value ||
                  "",
                version:
                  section.version !== undefined && section.version !== null
                    ? String(section.version)
                    : "",
                languageSpecificName:
                  section.language_specific_name ||
                  section.language_name ||
                  section.name ||
                  "",
                languageSpecificShortName:
                  section.language_specific_short_name ||
                  section.language_short_name ||
                  section.short_name ||
                  "",
                frontFile: section.front_pdf || null,
                backFile: section.back_pdf || null,
                collapsed: true,
              },
            );
          },
        );

        setCountryPdfSections(hydratedSections.length ? hydratedSections : []);
      });
  };

  const UpdateDataApi = async () => {
    const url = `${process.env.REACT_APP_API_URL}/actioncard/${id}`;
    const bodyFormData = new FormData();

    appendCommonFormData(bodyFormData);
    try {
      await syncNewTargetCountries();
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
        body: bodyFormData,
      });
      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
        return;
      }

      const iconData = await submitActionCardSmallIcon(id);
      if (iconData.error) {
        toast.error(iconData.error);
        return;
      }

      const pdfData = await submitActionCardPdfSections(id, true);
      if (pdfData.error) {
        toast.error(pdfData.error);
        return;
      }

      toast.success("Data Updated Successfully");
      history.push("/action-cards");
    } catch {
      toast.error("Something went wrong");
    }
  };

  const onSubmitHandler = (e) => {
    e.preventDefault();
    if (!validateAdditionalSections()) return;
    if (search) {
      UpdateDataApi();
    } else {
      syncNewTargetCountries()
        .then(() => postDataApi())
        .catch((error) => {
          toast.error(error.message || "Unable to save target countries");
        });
    }
  };

  const AcNumberInAddData = async () => {
    const url = `${process.env.REACT_APP_API_URL}/ac-number`;
    await fetch(url, {
      method: "get",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        const codeMessage = data.data;
        setAc_no(codeMessage);
      });
  };

  return (
    <>
      <div style={pageHeaderWrapStyle}>
        <FormPageHeader
          title={`${paramIdSeprator(search) ? "Update" : "Add"} Action-cards`}
          onBack={() => history.push("/action-cards")}
        />
      </div>

      <form onSubmit={onSubmitHandler}>
        <div style={formPanelStyle}>
          <h5 style={sectionTitleStyle}>Action-cards details</h5>
          <Box
            mt={2}
            mb={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Small Icon:
            </Typography>
            <input
              accept="image/png, image/jpeg, image/jpg, image/webp, image/heic, image/heif"
              type="file"
              id="small-icon-upload-image"
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
            <label
              htmlFor="small-icon-upload-image"
              style={{ position: "relative", display: "inline-block" }}
            >
              <CircleContainer>
                {smallIconPreview ? (
                  <ImagePreview
                    src={smallIconPreview}
                    alt="Small Icon Preview"
                  />
                ) : (
                  <span>Upload Icon.</span>
                )}
              </CircleContainer>

              {smallIconPreview && (
                <Button
                  onClick={handleRemoveImage}
                  sx={{
                    minWidth: "unset",
                    padding: 0,
                    position: "absolute",
                    top: "10px",
                    right: "-10px",
                    backgroundColor: "#f44336",
                    color: "#fff",
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    "&:hover": {
                      backgroundColor: "#d32f2f",
                    },
                  }}
                >
                  <RxCross2 size={16} />
                </Button>
              )}
            </label>
            {smallIconError && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {smallIconError}
              </Typography>
            )}
          </Box>
          <div className="row mb-3 align-items-center">
            <label className="col-12 col-md-2 col-form-label">AC No</label>
            <div className="col-12 col-md-4 mb-3 mb-md-0">
              <input
                type="text"
                className="form-control"
                value={ac_no}
                onChange={(e) => setAc_no(e.target.value)}
              />
            </div>
            <label className="col-12 col-md-2 col-form-label">
              Standard/Tailored
            </label>
            <div className="col-12 col-md-4">
              <Select
                value={standards}
                options={Standard}
                onChange={(value) => setStandards(value || Standard[0])}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12 col-md-6 mb-3 mb-md-0">
              <label>English Name</label>
              <input
                className="form-control"
                value={headerEnglishName}
                readOnly
              />
            </div>
            <div className="col-12 col-md-6">
              <label>Danish Name</label>
              <input
                className="form-control"
                value={danishName}
                onChange={(e) => setDanishName(e.target.value)}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12 col-md-6 mb-3 mb-md-0">
              <label>Short English Name</label>
              <input
                className="form-control"
                value={headerEnglishShortName}
                readOnly
              />
            </div>
            <div className="col-12 col-md-6">
              <label>Short Danish Name</label>
              <input
                className="form-control"
                value={danishShortName}
                onChange={(e) => setDanishShortName(e.target.value)}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12 col-md-6 mb-3 mb-md-0">
              <label>Purpose Type</label>
              <Select
                value={purposes}
                options={Purpose}
                onChange={setPurposes}
                isClearable
              />
            </div>

            <div className="col-12 col-md-6">
              <label>Format</label>
              <Select
                isMulti
                value={formats}
                options={Format}
                onChange={(value) => setFormats(value || [])}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-6 mb-3 mb-md-0">
              <label>Client Type</label>
              <Select
                isMulti
                value={clientTypes}
                options={ClientType}
                onChange={(value) => setClientTypes(value || [])}
              />
            </div>
            <div className="col-12 col-md-6">
              <label>Remarks</label>
              <textarea
                className="form-control"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="alert alert-warning" role="alert">
          <strong>Notice:</strong> Chirag mentioned that this section will be
          removed, so please update your data for available countries/languages.
          Once done, please let Chirag know so he can remove this section.
        </div>

        <div className="alert alert-warning">
          <h5 style={sectionTitleStyle}>Availability</h5>
          <div className="row">
            <div className="col-12 col-md-6 mb-3 mb-md-0">
              <label>Available countries/languages</label>
              <Select
                isMulti
                value={countrys}
                options={countryOptions}
                onChange={(value) => setCountrys(value || [])}
              />
            </div>

            <div className="col-12 col-md-6">
              <label>Version</label>
              <input
                type="text"
                className="form-control"
                value={versionNumber}
                onChange={(e) => setVersionNumber(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={formPanelStyle}>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
            <div className="mb-2 mb-md-0">
              <h5 className="mb-1">Country/languages</h5>
              <small className="text-muted">
                The {DEFAULT_COUNTRY_LANGUAGE_CODE} section is required and
                provides the header English names.
              </small>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={addCountryPdfSection}
              disabled={allCountryLanguagesUsed}
            >
              + Add Section
            </button>
          </div>
          {sectionErrors.default_country_language && (
            <small className="text-danger d-block mb-3">
              {sectionErrors.default_country_language.country_language_id}
            </small>
          )}

          {countryPdfSections.map((section, index) => (
            <div key={section.id} className="mb-4" style={sectionPanelStyle}>
              <div
                className="flex-column flex-md-row align-items-start align-items-md-center"
                style={sectionHeaderStyle}
              >
                <div className="mb-2 mb-md-0">
                  <h6 className="mb-0">{getSectionTitle(section, index)}</h6>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    style={iconButtonStyle}
                    onClick={() => toggleCountryPdfSection(section.id)}
                    title={section.collapsed ? "Expand" : "Collapse"}
                    aria-label={
                      section.collapsed ? "Expand section" : "Collapse section"
                    }
                  >
                    {section.collapsed ? (
                      <FiChevronDown size={20} />
                    ) : (
                      <FiChevronUp size={20} />
                    )}
                  </button>
                  <button
                    type="button"
                    style={removeButtonStyle}
                    onClick={() => removeCountryPdfSection(section.id)}
                    aria-label="Remove section"
                    disabled={isDefaultCountryLanguageSection(
                      section,
                      combinedCodeOptions,
                    )}
                  >
                    <MdDeleteOutline size={20} />
                  </button>
                </div>
              </div>

              {!section.collapsed && (
                <div style={sectionBodyStyle}>
                  <div className="row mb-3">
                    <div className="col-12 col-md-6 mb-3">
                      <label>
                        Country · Language{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Select
                        value={getSectionCountryLanguageOption(section)}
                        options={getSectionCountryLanguageOptions(section)}
                        onChange={(value) =>
                          updateCountryPdfSectionLanguage(section.id, value)
                        }
                        isClearable
                        isDisabled={isDefaultCountryLanguageSection(
                          section,
                          combinedCodeOptions,
                        )}
                      />
                      {sectionErrors[section.id]?.country_language_id && (
                        <small className="text-danger d-block mt-1">
                          {sectionErrors[section.id].country_language_id}
                        </small>
                      )}
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                      <label>Version</label>
                      <input
                        type="text"
                        className="form-control"
                        value={section.version}
                        onChange={(value) =>
                          updateCountryPdfSection(
                            section.id,
                            "version",
                            value.target.value,
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-12 col-md-6 mb-3 mb-md-0">
                      <label>
                        Language specific name{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          sectionErrors[section.id]?.languageSpecificName
                            ? "is-invalid"
                            : ""
                        }`}
                        value={section.languageSpecificName}
                        onChange={(e) =>
                          updateCountryPdfSection(
                            section.id,
                            "languageSpecificName",
                            e.target.value,
                          )
                        }
                      />
                      {sectionErrors[section.id]?.languageSpecificName && (
                        <div className="invalid-feedback">
                          {sectionErrors[section.id].languageSpecificName}
                        </div>
                      )}
                    </div>

                    <div className="col-12 col-md-6">
                      <label>Language specific short name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={section.languageSpecificShortName}
                        onChange={(e) =>
                          updateCountryPdfSection(
                            section.id,
                            "languageSpecificShortName",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-12 col-md-6 mb-3 mb-md-0">
                      <FileUploadBox
                        label="Front PDF"
                        file={section.frontFile}
                        setFile={(value) =>
                          updateCountryPdfSection(
                            section.id,
                            "frontFile",
                            value,
                          )
                        }
                        inputId={`frontFileInput_${section.id}`}
                        compact
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <FileUploadBox
                        label="Back PDF"
                        file={section.backFile}
                        setFile={(value) =>
                          updateCountryPdfSection(section.id, "backFile", value)
                        }
                        inputId={`backFileInput_${section.id}`}
                        compact
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={formPanelStyle}>
          <h5 style={sectionTitleStyle}>Select Questions</h5>
          <DraggableSelectList
            options={options}
            defaultValues={defaultQuestionIds}
            onChange={handleSelectedIds}
          />
        </div>

        <div className="text-center">
          <button type="submit" className="btn bg_color px-4">
            {paramIdSeprator(search) ? "Update" : "Add"}
          </button>
        </div>
      </form>
    </>
  );
};

export default AddActionCard;
